import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Shopify View Component
 *
 * Fetches products/variants from Shopify API and adds them to the tables.
 */
function ShopifyView({ onTableLoaded, setViewMode }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [productCount, setProductCount] = useState(0);
  const [variantCount, setVariantCount] = useState(0);
  const [inventoryLevels, setInventoryLevels] = useState(0);
  const [bundleCount, setBundleCount] = useState(0);

  const fetchShopifyProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/shopify/products`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch Shopify products');
      }

      const data = await response.json();

      setProductCount(data.product_count);
      setVariantCount(data.total_variants);
      setInventoryLevels(0);
      setLoading(false);

      // Add the Shopify table to the main tables
      if (onTableLoaded) {
        onTableLoaded(data.table);
      }

      // Switch back to tables view to see the rendered table
      if (setViewMode) {
        setViewMode('tables');
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchShopifyProductsWithInventory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/shopify/products-with-inventory`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch Shopify inventory data');
      }

      const data = await response.json();

      setProductCount(data.product_count);
      setVariantCount(data.variant_count);
      setInventoryLevels(data.levels);
      setBundleCount(0);
      setLoading(false);

      // Add the Shopify table to the main tables
      if (onTableLoaded) {
        onTableLoaded(data.table);
      }

      // Switch back to tables view to see the rendered table
      if (setViewMode) {
        setViewMode('tables');
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchShopifyProductsAll = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/shopify/products-all`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch Shopify products');
      }

      const data = await response.json();

      setProductCount(data.product_count);
      setVariantCount(data.variant_count);
      setBundleCount(data.bundle_count);
      setInventoryLevels(data.bundle_count > 0 ? 4 : 3);
      setLoading(false);

      // Add the Shopify table to the main tables
      if (onTableLoaded) {
        onTableLoaded(data.table);
      }

      // Switch back to tables view to see the rendered table
      if (setViewMode) {
        setViewMode('tables');
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="shopify-view">
      <div className="shopify-header">
        <h2>Shopify Products</h2>
        <p className="shopify-description">
          Import your Shopify products and variants as nested tables.
        </p>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={fetchShopifyProducts}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Loading...' : 'Fetch Products & Variants (2-Level)'}
          </button>

          <button
            onClick={fetchShopifyProductsWithInventory}
            disabled={loading}
            className="btn-primary"
            style={{ background: 'var(--accent-secondary, #059669)' }}
          >
            {loading ? 'Loading...' : 'Fetch with Inventory Locations (3-Level) ⭐'}
          </button>

          <button
            onClick={fetchShopifyProductsAll}
            disabled={loading}
            className="btn-primary"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 'bold' }}
          >
            {loading ? 'Loading...' : 'Variable-Depth Demo (3-4 Levels) 🚀'}
          </button>
        </div>

        {error && (
          <div className="error-message" style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c00'
          }}>
            <strong>Error:</strong> {error}
            <br />
            <small>
              Make sure you've configured SHOPIFY_STORE_URL and SHOPIFY_ACCESS_TOKEN in backend/.env
            </small>
          </div>
        )}

        {productCount > 0 && !loading && (
          <div className="shopify-stats" style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '6px'
          }}>
            <div style={{color: 'var(--text-success, green)', marginBottom: '0.5rem'}}>
              ✓ Table imported successfully!
            </div>
            <div style={{fontSize: '0.9rem'}}>
              {inventoryLevels === 4 ? (
                <>
                  <strong>🚀 Variable-Depth Heterogeneous Table Created!</strong>
                  <ul style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>
                    <li>Level 1: {productCount} Products (Title, Type, Vendor)</li>
                    <li>Level 2: {variantCount} Variants (Name, Price, SKU, Type)</li>
                    <li>Regular Products: 3 levels (→ Inventory Locations)</li>
                    <li>Bundle Products ({bundleCount} bundles): 4 levels (→ Components → Component Details)</li>
                  </ul>
                  <div style={{marginTop: '0.5rem', fontStyle: 'italic', fontWeight: 'bold', color: 'var(--accent, #3b82f6)'}}>
                    This demonstrates variable-depth nesting: different rows have different nested structures!
                  </div>
                </>
              ) : inventoryLevels === 3 ? (
                <>
                  <strong>3-Level Nested Table Created!</strong>
                  <ul style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>
                    <li>Level 1: {productCount} Products (Title, Type, Vendor)</li>
                    <li>Level 2: {variantCount} Variants (Name, Price, SKU, Total Qty)</li>
                    <li>Level 3: Inventory Locations (Location, City, Province, Available Qty)</li>
                  </ul>
                  <div style={{marginTop: '0.5rem', fontStyle: 'italic'}}>
                    Each level has different columns, showcasing heterogeneous nested tables!
                  </div>
                </>
              ) : (
                <>
                  Imported {productCount} products with {variantCount} total variants.
                </>
              )}
              <div style={{marginTop: '0.5rem'}}>
                Switch to "Tables" view to see it.
              </div>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-spinner" style={{
          textAlign: 'center',
          padding: '2rem',
          fontSize: '1.2rem',
          color: 'var(--text-secondary)'
        }}>
          Fetching products from Shopify...
        </div>
      )}

      {!loading && productCount === 0 && !error && (
        <div className="empty-state" style={{
          textAlign: 'center',
          padding: '3rem',
          color: 'var(--text-secondary)'
        }}>
          <p>Click "Fetch Shopify Products" to load your products</p>
          <p style={{fontSize: '0.9rem', marginTop: '1rem'}}>
            The table will be added to your tables and you'll be automatically switched to the Tables view.
          </p>
        </div>
      )}
    </div>
  );
}

export default ShopifyView;
